import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type LocationSearchItem = {
  street: string;
  city: string;
  stateCode: string;
  state: string;
  zip: string;
  countryCode: number;
  country: string;
  zone: string;
  countyAbbreviation: Maybe<string>;
  latitude: number;
  longitude: number;
  isCanada: boolean;
  isMexico: boolean;
  isHawaii: boolean;
  isAlaska: boolean;
};

export type SearchLocationGlobalTranzResponse = {
  locations: LocationSearchItem[];
  message?: string;
};

export type RefreshLocationsGlobalTranzResponse = {
  message: string;
  /**
   * Number of new locations that were created/initialized during the refresh.
   * Optional so the backend can evolve without breaking the FE.
   */
  createdCount?: number;
};

/** GlobalTranz country codes for SearchLocation API: 1=US, 2=CA, 3=CN, 4=MX */
export const GLOBALTRANZ_COUNTRY_CODES = {
  'United States': 1,
  Canada: 2,
  // China: 3,
  Mexico: 4,
} as const;

export async function searchLocationGlobalTranz(
  tmsID: number,
  keyword: string,
  countryCode?: number
): Promise<Result<SearchLocationGlobalTranzResponse, ApiError>> {
  try {
    const params = new URLSearchParams({
      tmsID: String(tmsID),
      keyword,
    });
    if (countryCode != null && countryCode > 0) {
      params.set('countryCode', String(countryCode));
    }
    const response = await axios.get<SearchLocationGlobalTranzResponse>(
      `/locations/search/globaltranz?${params.toString()}`
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'searchLocationGlobalTranz' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to search locations' });
    }

    if (error && isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err({
      message: error.response?.data.message || 'Failed to search locations',
    });
  }
}

/**
 * Triggers a GlobalTranz Address Book refresh for locations that match the given
 * company name for a specific customer. The backend will call the GTZ
 * `SearchAddresses` TMS API, upsert any new locations into our DB, and return
 * a summary message.
 *
 * NOTE: The exact endpoint path and payload shape can be adjusted when the
 * backend is implemented – update the URL/body here to match.
 */
export async function refreshLocationsGlobalTranz(
  tmsID: number,
  customerBK: string,
  name: string
): Promise<Result<RefreshLocationsGlobalTranzResponse, ApiError>> {
  try {
    const response = await axios.post<RefreshLocationsGlobalTranzResponse>(
      '/locations/globaltranz/addressbook/refresh',
      {
        tmsID,
        customerBK,
        name,
      }
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'refreshLocationsGlobalTranz' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to refresh locations from GlobalTranz' });
    }

    if (error && isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err({
      message:
        error.response?.data.message ||
        'Failed to refresh locations from GlobalTranz',
    });
  }
}
